import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { modelService } from '../../services/api';
import CheckpointInspector from './CheckpointInspector';

// Model-builder v2 phase definitions
const PHASES = [
  { id: 1, name: 'Data Understanding', key: '01_analyze_data' },
  { id: 2, name: 'Data Preparation', key: '02_prepare_data' },
  { id: 3, name: 'Baseline Models', key: '03_build_baselines' },
  { id: 4, name: 'Model Search', key: '04_search_models' },
  { id: 5, name: 'Final Evaluation', key: '05_evaluate_final' },
  { id: 6, name: 'Packaging', key: '06_package_final_model' },
];

function BuildProgress({ modelId, status, errorMessage, onRebuild }) {
  const navigate = useNavigate();
  const [checkpoints, setCheckpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [fullRebuilding, setFullRebuilding] = useState(false);
  const [inspectingCheckpoint, setInspectingCheckpoint] = useState(null);

  const loadCheckpoints = useCallback(async () => {
    try {
      const data = await modelService.getCheckpoints(modelId);
      setCheckpoints(data.checkpoints || []);
    } catch (error) {
      setCheckpoints([]);
    } finally {
      setLoading(false);
    }
  }, [modelId]);

  useEffect(() => {
    if (modelId) {
      loadCheckpoints();
    }
  }, [modelId, loadCheckpoints]);

  const handleSmartResume = async () => {
    setRebuilding(true);
    try {
      await onRebuild();
    } finally {
      setRebuilding(false);
    }
  };

  const handleFullRebuild = async () => {
    setFullRebuilding(true);
    try {
      // Delete all checkpoints
      await modelService.deleteCheckpoints(modelId);
      // Trigger rebuild
      await onRebuild();
    } catch (error) {
      console.error('Full rebuild failed:', error);
    } finally {
      setFullRebuilding(false);
    }
  };

  const handleEditWithAI = (phase) => {
    // Extract experiment ID from modelId
    const experimentId = modelId.includes('/') ? modelId.split('/')[1] : modelId;

    // Navigate to chat with checkpoint context
    navigate(`/chat?fix_checkpoint=${phase.key}&experiment_id=${experimentId}&phase_name=${encodeURIComponent(phase.name)}`);
  };

  const getPhaseStatus = (phase) => {
    const checkpoint = checkpoints.find((cp) => cp.phase === phase.key);
    if (checkpoint) return 'completed';

    if (status === 'failed') {
      const lastCompletedPhaseId = Math.max(0, ...checkpoints.map((cp) => {
        const p = PHASES.find((ph) => ph.key === cp.phase);
        return p ? p.id : 0;
      }));
      if (phase.id === lastCompletedPhaseId + 1) return 'failed';
    }

    if (status === 'processing') {
      const lastCompletedPhaseId = Math.max(0, ...checkpoints.map((cp) => {
        const p = PHASES.find((ph) => ph.key === cp.phase);
        return p ? p.id : 0;
      }));
      if (phase.id === lastCompletedPhaseId + 1) return 'in_progress';
    }

    return 'pending';
  };

  const shouldShow = status === 'processing' || status === 'failed' || checkpoints.length > 0;
  if (!shouldShow) return null;

  const completedCount = Math.min(checkpoints.length, PHASES.length);
  const progressPercent = Math.min((checkpoints.length / PHASES.length) * 100, 100);

  // Get the last completed checkpoint for smart resume messaging
  const lastCheckpoint = checkpoints.length > 0
    ? PHASES.find(p => p.key === checkpoints[checkpoints.length - 1].phase)
    : null;

  // Calculate which phase failed
  const failedPhase = status === 'failed' && lastCheckpoint
    ? PHASES.find(p => p.id === lastCheckpoint.id + 1)
    : null;

  return (
    <div className="border-t border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50 mb-8">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
              Build Pipeline
            </h3>
            <div className="flex items-baseline gap-6 mt-2">
              <span className="font-mono text-2xl font-light text-gray-900 dark:text-white">
                {completedCount}<span className="text-gray-400 dark:text-gray-500">/{PHASES.length}</span>
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {progressPercent.toFixed(0)}% complete
              </span>
            </div>
          </div>

          {status === 'failed' && checkpoints.length > 0 && lastCheckpoint && (
            <div className="text-right flex flex-col gap-3">
              <div>
                <button
                  onClick={handleSmartResume}
                  disabled={rebuilding || fullRebuilding}
                  className="
                    text-xs font-medium uppercase tracking-wider
                    text-gray-700 dark:text-gray-300
                    hover:text-gray-900 dark:hover:text-white
                    transition-colors
                    disabled:opacity-50 disabled:cursor-not-allowed
                    border-b border-gray-400 dark:border-gray-500
                    hover:border-gray-900 dark:hover:border-white
                    pb-0.5
                  "
                >
                  {rebuilding ? 'Resuming' : `Resume from Phase ${lastCheckpoint.id + 1}`}
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                  Skips phases 1-{lastCheckpoint.id}
                </p>
              </div>
              <div>
                <button
                  onClick={handleFullRebuild}
                  disabled={rebuilding || fullRebuilding}
                  className="
                    text-xs font-medium uppercase tracking-wider
                    text-gray-500 dark:text-gray-400
                    hover:text-gray-700 dark:hover:text-gray-200
                    transition-colors
                    disabled:opacity-50 disabled:cursor-not-allowed
                    border-b border-gray-300 dark:border-gray-600
                    hover:border-gray-500 dark:hover:border-gray-400
                    pb-0.5
                  "
                >
                  {fullRebuilding ? 'Starting Fresh' : 'Full Rebuild'}
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                  Deletes all checkpoints
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Progress Visualization */}
        <div className="space-y-3">
          {PHASES.map((phase) => {
            const phaseStatus = getPhaseStatus(phase);

            return (
              <div key={phase.id} className="flex items-center gap-4 group">
                {/* Phase Number */}
                <div className="w-8 text-right">
                  <span className={`
                    font-mono text-xs
                    ${phaseStatus === 'completed' ? 'text-emerald-600 dark:text-emerald-400' :
                      phaseStatus === 'failed' ? 'text-rose-600 dark:text-rose-400' :
                      phaseStatus === 'in_progress' ? 'text-blue-600 dark:text-blue-400' :
                      'text-gray-400 dark:text-gray-500'}
                  `}>
                    {phase.id}
                  </span>
                </div>

                {/* Phase Name */}
                <div className="flex-1 min-w-0">
                  <div className={`
                    text-sm
                    ${phaseStatus === 'completed' ? 'text-gray-900 dark:text-white' :
                      phaseStatus === 'failed' ? 'text-rose-900 dark:text-rose-200' :
                      phaseStatus === 'in_progress' ? 'text-gray-900 dark:text-white' :
                      'text-gray-500 dark:text-gray-400'}
                  `}>
                    {phase.name}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="flex-1 min-w-0">
                  <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`
                        h-full transition-all duration-700 ease-out
                        ${phaseStatus === 'completed' ? 'bg-emerald-500 dark:bg-emerald-400 w-full' :
                          phaseStatus === 'failed' ? 'bg-rose-500 dark:bg-rose-400 w-full' :
                          phaseStatus === 'in_progress' ? 'bg-blue-500 dark:bg-blue-400 w-1/2' :
                          'w-0'}
                      `}
                      style={phaseStatus === 'in_progress' ? {
                        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                      } : {}}
                    />
                  </div>
                </div>

                {/* Status Indicator */}
                <div className="w-48 text-right flex items-center justify-end gap-3">
                  {phaseStatus === 'completed' && (
                    <>
                      <button
                        onClick={() => setInspectingCheckpoint(phase)}
                        className="
                          text-xs text-gray-500 dark:text-gray-400
                          hover:text-gray-900 dark:hover:text-white
                          transition-colors
                          border-b border-gray-300 dark:border-gray-600
                          hover:border-gray-900 dark:hover:border-white
                          pb-0.5
                        "
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleEditWithAI(phase)}
                        className="
                          text-xs text-blue-600 dark:text-blue-400
                          hover:text-blue-700 dark:hover:text-blue-300
                          transition-colors
                          border-b border-blue-400 dark:border-blue-500
                          hover:border-blue-700 dark:hover:border-blue-300
                          pb-0.5
                        "
                      >
                        Ask Plexe
                      </button>
                      <span className="text-xs text-emerald-600 dark:text-emerald-400 font-mono">
                        Done
                      </span>
                    </>
                  )}
                  {phaseStatus === 'failed' && (
                    <span className="text-xs text-rose-600 dark:text-rose-400 font-mono">
                      Failed
                    </span>
                  )}
                  {phaseStatus === 'in_progress' && (
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-mono">
                      Running
                    </span>
                  )}
                  {phaseStatus === 'pending' && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                      —
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Error Detail */}
        {status === 'failed' && errorMessage && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Error Details
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {errorMessage}
            </p>
          </div>
        )}
      </div>

      {/* Checkpoint Inspector Modal */}
      {inspectingCheckpoint && (
        <CheckpointInspector
          modelId={modelId}
          phase={inspectingCheckpoint.key}
          phaseName={inspectingCheckpoint.name}
          onClose={() => setInspectingCheckpoint(null)}
        />
      )}
    </div>
  );
}

export default BuildProgress;
